import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ToastTone } from './store';

/**
 * One snackbar pill — the exact visual contract from the toaster spec: a slim
 * dark pill with a status icon, a single line of text, and a dismiss button.
 * Nothing else is interactive. A timer rail across the bottom shrinks over
 * `dur` seconds, then the pill auto-dismisses; hovering pauses it.
 *
 * The pill owns its own timer + exit animation so the rail and the removal
 * can't drift apart: on dismiss it plays `toastOut`, then calls `onClose(id)`.
 */

type Props = {
  id: string;
  tone?: ToastTone;
  message: string;
  /** Auto-dismiss delay in seconds. */
  dur?: number;
  onClose: (id: string) => void;
};

const EXIT_MS = 200;

// Solid tone colours — the status-icon circle background and the timer-rail bar.
const TONE_COLOR: Record<ToastTone, string> = {
  good: 'var(--good-500)',
  bad: 'var(--bad-500)',
  info: 'var(--info-500)',
  brand: 'var(--brand-500)',
  warn: 'var(--warn-500)',
};

// Fixed white-on-tone glyphs at strokeWidth 2.6 — finer than the monochrome
// `Icon.tsx` set (1.75px, currentColor), so a local glyph map is the right
// call here, the same way `ProviderIcon` opts out for brand marks.
const TONE_GLYPH: Record<ToastTone, ReactNode> = {
  good: <polyline points="5 13 9 17 19 7" />,
  bad: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  info: (
    <>
      <line x1="12" y1="11" x2="12" y2="17" />
      <line x1="12" y1="7.5" x2="12.01" y2="7.5" />
    </>
  ),
  brand: <polygon points="13 2 4 14 11 14 10 22 19 9 12 9" />,
  warn: (
    <>
      <path d="M12 4 L2.5 20 L21.5 20 Z" />
      <line x1="12" y1="10" x2="12" y2="14" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
};

export function Toast({ id, tone = 'good', message, dur = 5, onClose }: Props) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);

  // Remaining-time bookkeeping so hover pause/resume stays in lock-step with
  // the CSS timer rail (which pauses via `animationPlayState`).
  const remainingRef = useRef(dur * 1000);
  const startRef = useRef(0);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const color = TONE_COLOR[tone];

  // Begin the exit animation, then remove from the store once it has played.
  const beginLeave = () => {
    if (exitTimer.current) return; // already leaving
    clearTimeout(dismissTimer.current);
    setLeaving(true);
    exitTimer.current = setTimeout(() => onClose(id), EXIT_MS);
  };

  const schedule = () => {
    startRef.current = Date.now();
    dismissTimer.current = setTimeout(beginLeave, remainingRef.current);
  };

  const pause = () => {
    if (leaving) return;
    clearTimeout(dismissTimer.current);
    remainingRef.current -= Date.now() - startRef.current;
    setPaused(true);
  };

  const resume = () => {
    if (leaving) return;
    setPaused(false);
    schedule();
  };

  useEffect(() => {
    schedule();
    return () => {
      clearTimeout(dismissTimer.current);
      clearTimeout(exitTimer.current);
    };
    // Mount-only: the pill's identity never changes for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="status"
      className={leaving ? 'toast leaving' : 'toast'}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <span
        aria-hidden
        style={{
          width: 18,
          height: 18,
          flexShrink: 0,
          borderRadius: '50%',
          background: color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={11}
          height={11}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth={2.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {TONE_GLYPH[tone]}
        </svg>
      </span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.3,
          // Wrap the full message rather than clip it — bulk-action rollups run
          // past one line. `overflowWrap: anywhere` breaks long unbreakable
          // tokens (emails, codes) so nothing escapes the pill's max-width.
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        }}
      >
        {message}
      </span>

      <button type="button" className="toast-x" aria-label="Dismiss" onClick={beginLeave}>
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>

      <span aria-hidden className="toast-rail">
        <span
          style={{
            display: 'block',
            height: '100%',
            background: color,
            transformOrigin: 'left',
            animation: `toastTimer ${dur}s linear forwards`,
            animationPlayState: paused || leaving ? 'paused' : 'running',
          }}
        />
      </span>
    </div>
  );
}
